import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';
import { AuthService } from '../../services/auth.service';

interface NavItem {
  /** Clé i18n. Le HTML applique `| transloco` pour afficher le libellé. */
  label: string;
  icon: string;
  route?: string;
  badge?: string;
  children?: NavItem[];
  group?: string;
  roles?: string[];
}

interface NavGroup {
  /** Clé i18n. */
  title: string;
  items: NavItem[];
  roles?: string[];
}

const STORAGE_KEY = 'vd-sidebar-collapsed';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslocoModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
  host: {
    '[class.collapsed-host]': 'collapsed()',
  },
})
export class SidebarComponent implements OnInit, OnDestroy {
  readonly collapsed = signal<boolean>(false);
  readonly expandedGroups = signal<Record<string, boolean>>({});
  readonly activeUrl = signal<string>('');

  private readonly auth = inject(AuthService);

  // ⚠️ `label` et `title` sont des CLÉS i18n. Le HTML les passe au pipe
  // `transloco`. Ne pas mettre de string FR ici directement (sinon AR/EN
  // afficheraient le libellé FR).
  private readonly allGroups: NavGroup[] = [
    {
      title: 'sidebar.group.overview',
      items: [
        { label: 'nav.home', icon: 'space_dashboard', route: '/Accueil' },
        { label: 'sidebar.item.social_onboarding', icon: 'rocket_launch', route: '/social/onboarding' },
        { label: 'sidebar.item.inbox_unified', icon: 'forum', route: '/inbox' },
        // Les publications sont accessibles via le module Marketing (groupe
        // ci-dessous, item « Publications »). On évite le doublon ici.
        { label: 'nav.chatbot', icon: 'smart_toy', route: '/social/chatbot' },
        { label: 'sidebar.item.support_center', icon: 'support_agent', route: '/support' },
        { label: 'sidebar.item.help_center', icon: 'menu_book', route: '/docs' },
        { label: 'sidebar.item.activities', icon: 'history', route: '/activities' },
      ],
    },
    {
      // Admin en POSITION 2 (juste sous Overview) : ces outils de pilotage
      // etaient relegues en bas d'une sidebar de ~3000px -> invisibles sans
      // scroller 3 ecrans. Un admin doit les voir immediatement.
      title: 'sidebar.group.admin',
      roles: ['admin'],
      items: [
        { label: 'sidebar.item.supervision', icon: 'shield_moon', route: '/admin/supervision', roles: ['admin'] },
        { label: 'sidebar.item.onboarding_review', icon: 'how_to_reg', route: '/admin/onboarding-review', roles: ['admin', 'support'] },
        { label: 'sidebar.item.prospection', icon: 'radar', route: '/admin/prospection', roles: ['admin', 'support'] },
        { label: 'sidebar.item.ai_provider', icon: 'psychology', route: '/admin/ai-provider' },
        { label: 'sidebar.item.vendor_ai', icon: 'tune', route: '/admin/vendor-ai-settings', roles: ['admin'] },
        { label: 'sidebar.item.blog_admin', icon: 'article', route: '/admin/blog', roles: ['admin'] },
        { label: 'sidebar.item.ai_security_admin', icon: 'security', route: '/admin/security', roles: ['admin'] },
        { label: 'sidebar.item.api_keys_admin', icon: 'vpn_key', route: '/admin/api-keys' },
      ],
    },
    {
      // Telephonie en position 3 : nouveau module phare, au-dessus du pli.
      title: 'sidebar.group.telephony',
      items: [
        { label: 'sidebar.item.callcenter', icon: 'call', route: '/telephonie', roles: ['vendor', 'admin'] },
      ],
    },
    {
      title: 'sidebar.group.marketing',
      items: [
        {
          label: 'nav.marketing',
          icon: 'campaign',
          children: [
            { label: 'marketing.tab.atelier',       icon: 'auto_awesome',       route: '/marketing/atelier' },
            { label: 'marketing.tab.posts',         icon: 'campaign',           route: '/marketing/posts' },
            { label: 'marketing.tab.reels_stories', icon: 'movie',              route: '/marketing/reels-stories' },
            { label: 'marketing.tab.campaigns',     icon: 'bar_chart',          route: '/marketing/campaigns' },
            { label: 'marketing.googleads.title',   icon: 'ads_click',          route: '/marketing/google-ads' },
            { label: 'marketing.tab.leads',         icon: 'fact_check',         route: '/marketing/leads' },
            { label: 'marketing.tab.prospection',   icon: 'travel_explore',     route: '/marketing/prospection' },
            { label: 'marketing.tab.catalog',       icon: 'inventory_2',        route: '/marketing/catalog' },
            { label: 'marketing.tab.insights',      icon: 'analytics',          route: '/marketing/insights' },
            { label: 'marketing.tab.marketer',      icon: 'psychology',         route: '/ai-agents/marketer' },
            { label: 'marketing.tab.tiktok',        icon: 'music_note',         route: '/marketing/tiktok', badge: 'marketing.badge_soon' },
          ],
        },
      ],
    },
    {
      title: 'sidebar.group.commerce',
      items: [
        { label: 'sidebar.item.pos', icon: 'point_of_sale', route: '/pos' },
        { label: 'sidebar.item.pos_card_payments', icon: 'credit_score', route: '/pos/card-payments', roles: ['vendor', 'admin'] },
        { label: 'sidebar.item.catalog', icon: 'inventory_2', route: '/produits' },
        { label: 'nav.orders', icon: 'shopping_cart', route: '/commande' },
        { label: 'sidebar.item.order_links', icon: 'sell', route: '/order-links' },
        { label: 'sidebar.item.rentals', icon: 'event_available', route: '/rentals' },
        { label: 'nav.customers', icon: 'people', route: '/clients' },
        { label: 'nav.stores', icon: 'storefront', route: '/Boutique' },
        { label: 'sidebar.item.pos_settings', icon: 'manage_accounts', route: '/pos/settings', roles: ['vendor', 'admin'] },
      ],
    },
    {
      title: 'sidebar.group.logistics',
      items: [
        { label: 'sidebar.item.scan_hub', icon: 'qr_code_scanner', route: '/scan' },
        { label: 'sidebar.item.labels_hub', icon: 'print', route: '/etiquettes' },
        { label: 'sidebar.item.inventory_warehouses', icon: 'warehouse', route: '/inventaire' },
        { label: 'sidebar.item.stock_products', icon: 'inventory_2', route: '/inventaire/stock' },
        { label: 'sidebar.item.stock_moves', icon: 'receipt_long', route: '/inventaire/mouvements' },
      ],
    },
    {
      // Dropshipping (Lot 1) — visible aux vendor/admin uniquement.
      // Tant que la signature CGU + mandat n'est pas effectuée, l'item
      // ouvre le wizard d'onboarding ; après activation, il deviendra
      // un hub (catalogue partenaires, RFQ, dashboard).
      title: 'sidebar.group.dropshipping',
      roles: ['vendor', 'admin'],
      items: [
        { label: 'sidebar.item.dropship_onboarding', icon: 'storefront', route: '/dropship/onboarding' },
        { label: 'sidebar.item.dropship_dashboard', icon: 'dashboard', route: '/dropship/dashboard' },
        { label: 'sidebar.item.dropship_partnerships', icon: 'handshake', route: '/dropship/partnerships' },
        { label: 'sidebar.item.dropship_supplier_policy', icon: 'rule', route: '/dropship/supplier-policy' },
        { label: 'sidebar.item.dropship_rfq', icon: 'request_quote', route: '/dropship/rfq/new' },
        { label: 'sidebar.item.admin_escrow', icon: 'account_balance', route: '/admin/escrow', roles: ['admin'] },
      ],
    },
    {
      title: 'sidebar.group.field_team',
      items: [
        { label: 'nav.agents', icon: 'badge', route: '/agents' },
        { label: 'sidebar.item.planning', icon: 'calendar_month', route: '/agents/planning' },
        { label: 'sidebar.item.team_messages', icon: 'chat', route: '/agents/messages' },
        { label: 'sidebar.item.roles', icon: 'workspace_premium', route: '/agents/roles', roles: ['vendor', 'admin'] },
      ],
    },
    {
      title: 'sidebar.group.tasks',
      items: [
        { label: 'sidebar.item.tasks_board', icon: 'view_kanban', route: '/tasks/board' },
        { label: 'sidebar.item.tasks_gantt', icon: 'view_timeline', route: '/tasks/gantt' },
        { label: 'sidebar.item.my_tasks', icon: 'checklist', route: '/me/tasks' },
      ],
    },
    {
      title: 'sidebar.group.intelligence',
      roles: ['vendor', 'admin'],
      items: [
        { label: 'sidebar.item.ai_modules', icon: 'extension', route: '/modules' },
        { label: 'nav.ai_team', icon: 'smart_toy', route: '/ai-agents' },
        { label: 'sidebar.item.right_hand', icon: 'support_agent', route: '/ai-agents/right-hand' },
        { label: 'sidebar.item.marketer', icon: 'psychology', route: '/ai-agents/marketer' },
        { label: 'sidebar.item.ai_recruit', icon: 'person_add', route: '/ai-agents/recruitment' },
        { label: 'sidebar.item.mcp_servers', icon: 'hub', route: '/ai-agents/mcp' },
        { label: 'sidebar.item.ai_security', icon: 'security', route: '/ai-agents/security' },
      ],
    },
    {
      title: 'sidebar.group.my_site',
      roles: ['vendor', 'admin'],
      items: [
        { label: 'nav.sites', icon: 'language', route: '/sites' },
        { label: 'sidebar.item.create_site', icon: 'add_circle_outline', route: '/onboarding-site' },
      ],
    },
    {
      title: 'sidebar.group.shipping',
      items: [
        { label: 'sidebar.item.shipments', icon: 'local_shipping', route: '/expeditions' },
        { label: 'sidebar.item.shipping_settings', icon: 'settings_input_component', route: '/expedition', roles: ['vendor', 'admin'] },
        // couriers_tn (/tunisie-delivery) déprécié : Intigo/Droppex vivent
        // désormais dans le hub /expeditions (unification ship01).
        { label: 'sidebar.item.couriers_all', icon: 'group', route: '/livreurs' },
      ],
    },
    {
      title: 'sidebar.group.tools',
      items: [
        { label: 'sidebar.item.calculator', icon: 'calculate', route: '/calculator' },
        { label: 'sidebar.item.cashbox', icon: 'account_balance_wallet', route: '/Gestionnaire' },
        { label: 'sidebar.item.accounting', icon: 'account_balance', route: '/comptabilite', roles: ['vendor', 'admin'] },
        { label: 'sidebar.item.kimbyala', icon: 'event_note', route: '/kimbyala', roles: ['vendor', 'admin'] },
        { label: 'sidebar.item.employees', icon: 'badge', route: '/Employe' },
      ],
    },
    {
      title: 'sidebar.group.account',
      items: [
        { label: 'navbar.my_profile', icon: 'account_circle', route: '/profile' },
        { label: 'nav.settings', icon: 'settings', route: '/settings' },
        { label: 'sidebar.item.notifications_settings', icon: 'notifications', route: '/settings/notifications' },
        { label: 'sidebar.item.push_settings', icon: 'notifications_active', route: '/settings/push' },
        { label: 'sidebar.item.channels_settings', icon: 'hub', route: '/settings/channels' },
        { label: 'sidebar.item.whatsapp_settings', icon: 'chat', route: '/settings/whatsapp', roles: ['vendor', 'admin'] },
        { label: 'sidebar.item.api_keys', icon: 'vpn_key', route: '/settings/api-keys' },
        { label: 'sidebar.item.mail_servers', icon: 'forward_to_inbox', route: '/settings/mail', roles: ['vendor', 'admin'] },
      ],
    },
  ];

  readonly groups = computed<NavGroup[]>(() => {
    return this.allGroups
      .filter((g) => !g.roles || g.roles.some((r) => this.auth.hasRole(r)))
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) => !it.roles || it.roles.some((r) => this.auth.hasRole(r)),
        ),
      }))
      .filter((g) => g.items.length > 0);
  });

  readonly width = computed(() => (this.collapsed() ? 'var(--vd-sidebar-width-collapsed)' : 'var(--vd-sidebar-width)'));

  private routerSub?: Subscription;

  constructor(private router: Router) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'true') this.collapsed.set(true);
    } catch {}
  }

  ngOnInit(): void {
    this.activeUrl.set(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.activeUrl.set(e.urlAfterRedirects);
        for (const g of this.groups()) {
          for (const item of g.items) {
            if (item.children?.some((c) => c.route && e.urlAfterRedirects.startsWith(c.route))) {
              this.expandedGroups.update((prev) => ({ ...prev, [item.label]: true }));
            }
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  toggleCollapse(): void {
    this.collapsed.update((v) => !v);
    try { localStorage.setItem(STORAGE_KEY, String(this.collapsed())); } catch {}
  }

  toggleGroup(label: string): void {
    if (this.collapsed()) {
      this.collapsed.set(false);
    }
    this.expandedGroups.update((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  isActive(item: NavItem): boolean {
    const url = this.activeUrl();
    if (item.route) return url === item.route || url.startsWith(item.route + '/');
    if (item.children) return item.children.some((c) => this.isActive(c));
    return false;
  }

  isGroupExpanded(label: string): boolean {
    return !!this.expandedGroups()[label];
  }
}
